import React, { useEffect, useId, useRef } from 'react';

/**
 * The app's confirmation dialog for exam flows (digital and paper): a centred
 * card over a blurred backdrop, a bottom sheet on phones. Replaces the
 * browser's own confirm() box.
 *
 * `actions`: [{ label, onClick, primary?, danger? }] — the last one gets focus.
 * `onCancel`: called on Escape (usually the same as the "stay" action).
 * `wide`: a larger, scrollable card for long content.
 */
const ConfirmDialog = ({ title, children, actions, onCancel, wide = false }) => {
  const titleId = useId();
  const primaryRef = useRef(null);

  useEffect(() => { primaryRef.current?.focus(); }, []);
  useEffect(() => {
    if (!onCancel) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={`w-full ${wide ? 'max-w-3xl max-h-[88vh] overflow-y-auto' : 'max-w-md'} rounded-2xl bg-white p-6 shadow-2xl animate-scale-in`}
      >
        <h2 id={titleId} className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          {title}
        </h2>
        <div className="mt-3 text-gray-700">{children}</div>
        <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
          {actions.map((a, i) => {
            let tone = 'bg-gray-100 text-gray-900 hover:bg-gray-200';
            if (a.primary) tone = 'bg-[#111] text-white hover:bg-black';
            if (a.danger) tone = 'bg-red-600 text-white hover:bg-red-700';
            return (
              <button
                key={a.label}
                ref={i === actions.length - 1 ? primaryRef : undefined}
                type="button"
                onClick={a.onClick}
                className={`min-h-[48px] px-5 rounded-xl font-semibold transition-all duration-150 active:scale-[0.98] touch-manipulation focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#111] ${tone}`}
              >
                {a.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
